declare global {
  interface Window {
    aiaibot?: {
      state?: { loaded?: boolean }
      show: () => void
      hide: () => void
    }
  }
}

const AIAIBOT_SCRIPT_SRC = 'https://chat.aiaibot.com/bootstrap.js'

// Keep in sync with the trigger/path configuration in the aiaibot dashboard.
// The non-German entries are the per-language Nuxt routes / Drupal aliases of the
// shop and publishing pages; they must be updated here if the aliases change in
// the CMS.
const ALLOWED_PATH_PREFIXES = [
  '/shop',
  '/en/shop',
  '/fr/shop',
  '/it/shop',
  '/bereiche/verlag',
  '/en/areas/publishing',
  '/fr/domaines/edititions-careum',
  '/it/settori/casa-editrice',
]

const VISIBILITY_POLL_INTERVAL_MS = 200
const MAX_VISIBILITY_POLL_ATTEMPTS = 150

let visibilityPollInterval: number | undefined

const isAllowedPath = (path: string) =>
  ALLOWED_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))

const isAiaibotPresent = () => {
  try {
    return Boolean(window.aiaibot || document.querySelector(`script[src="${AIAIBOT_SCRIPT_SRC}"]`))
  } catch {
    return false
  }
}

const injectScript = (key: string) => {
  if (isAiaibotPresent()) return

  try {
    const script = document.createElement('script')
    script.src = AIAIBOT_SCRIPT_SRC
    script.setAttribute('data-aiaibot-key', key)
    script.defer = true
    document.body.appendChild(script)
  } catch {
    // A blocked or unavailable script must not affect the rest of the app.
  }
}

const clearVisibilityPoll = () => {
  if (visibilityPollInterval === undefined) return

  const interval = visibilityPollInterval
  visibilityPollInterval = undefined

  try {
    window.clearInterval(interval)
  } catch {
    // Clearing a failed poll must not affect the rest of the app.
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const aiaibotKey = config.public.aiaibotKey

  if (!aiaibotKey) return

  const router = useRouter()

  const applyVisibility = () => {
    if (visibilityPollInterval !== undefined) return

    let attempts = 0

    try {
      visibilityPollInterval = window.setInterval(() => {
        attempts += 1

        try {
          const aiaibot = window.aiaibot

          if (aiaibot?.state?.loaded) {
            clearVisibilityPoll()

            if (isAllowedPath(router.currentRoute.value.path)) {
              aiaibot.show?.()
            } else {
              aiaibot.hide?.()
            }

            return
          }
        } catch {
          // An unexpected widget API shape degrades to no chatbot.
        }

        if (attempts >= MAX_VISIBILITY_POLL_ATTEMPTS) clearVisibilityPoll()
      }, VISIBILITY_POLL_INTERVAL_MS)
    } catch {
      visibilityPollInterval = undefined
    }
  }

  // The dashboard triggers may not cover the translated paths, so visibility is
  // applied explicitly on every route event instead of relying on the widget's
  // own path rules.
  const syncWithRoute = (path: string) => {
    if (isAllowedPath(path)) injectScript(aiaibotKey)
    if (isAiaibotPresent()) applyVisibility()
  }

  nuxtApp.hook('app:mounted', () => syncWithRoute(router.currentRoute.value.path))

  router.afterEach((to) => syncWithRoute(to.path))
})

export {}
